// src/routes.jsx
import React, { Suspense, lazy } from "react";
import { Routes, Route } from "react-router-dom";
import PageLoader from "./components/PageLoader";

// Lazy load pages for code splitting optimization
const SplashScreen = lazy(() => import("./pages/SplashScreen"));
const WelcomePage = lazy(() => import("./pages/WelcomePage"));
const GeneralPin = lazy(() => import("./pages/GeneralPin"));
const SignIn = lazy(() => import("./pages/SignIn"));
const SignUp = lazy(() => import("./pages/SignUp"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const SubCategory = lazy(() => import("./pages/SubCategory"));
const Profile = lazy(() => import("./pages/Profile"));
const Licence = lazy(() => import("./pages/Licence"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const Collections = lazy(() => import("./pages/Collections"));
const CollectionDetail = lazy(() => import("./pages/CollectionDetail"));
const LikedCollection = lazy(() => import("./pages/LikedCollection"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Portals = lazy(() => import("./pages/Portals"));
const ClientPortal = lazy(() => import("./pages/ClientPortal"));
const ClientLiked = lazy(() => import("./pages/ClientLiked"));
const ClientSharedItem = lazy(() => import("./pages/ClientSharedItem"));
const TestPage = lazy(() => import("./pages/TestPage"));

// Components that are always needed can remain static or be lazy loaded too
import ProtectedRoute from "./components/ProtectedRoute";
import BottomNav from "./components/BottomNav";
import PublicRoute from "./components/PublicRoute";
import AnimatedPage from './components/AnimatedPage';
import TestCategoryButton from './components/TestCategoryButton';
import DoubleTapTest from './components/DoubleTapTest';


const AppRoutes = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route
          path="/"
          element={<SplashScreen />}
        />
        <Route
          path="/test"
          element={<TestPage />}
        />
        <Route
          path="/test-category"
          element={<TestCategoryButton />}
        />
        <Route
          path="/test-double-tap"
          element={<DoubleTapTest />}
        />

        {/* Public Routes */}
        <Route
          path="/welcome"
          element={
            <PublicRoute>
              <AnimatedPage>
                <WelcomePage />
              </AnimatedPage>
            </PublicRoute>
          }
        />
        <Route
          path="/pin"
          element={
            <PublicRoute>
              <AnimatedPage>
                <GeneralPin />
              </AnimatedPage>
            </PublicRoute>
          }
        />
        <Route
          path="/signin"
          element={
            <PublicRoute>
              <AnimatedPage>
                <SignIn />
              </AnimatedPage>
            </PublicRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <AnimatedPage>
                <SignUp />
              </AnimatedPage>
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <AnimatedPage>
                <ResetPassword />
              </AnimatedPage>
            </PublicRoute>
          }
        />

        {/* --- START OF CORRECTED PUBLIC PORTAL ROUTES --- */}
        <Route
          path="/portal/view/:accessKey"
          element={
            <AnimatedPage>
              <ClientPortal />
            </AnimatedPage>
          }
        />
        <Route
          path="/portal/view/:accessKey/collection/:collectionId"
          element={
            <AnimatedPage>
              <ClientSharedItem />
            </AnimatedPage>
          }
        />
        <Route
          path="/portal/view/:accessKey/gallery/:categoryName/:subCategoryName"
          element={
            <AnimatedPage>
              <ClientSharedItem />
            </AnimatedPage>
          }
        />
        {/* --- END OF CORRECTED PUBLIC PORTAL ROUTES --- */}


        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <AnimatedPage>
                <Dashboard />
                <BottomNav />
              </AnimatedPage>
            </ProtectedRoute>
          }
        />

        <Route
          path="/subcategory/:categoryName"
          element={
            <ProtectedRoute>
              <AnimatedPage>
                <SubCategory />
              </AnimatedPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="/gallery/:categoryName/:subCategoryName"
          element={
            <ProtectedRoute>
              <AnimatedPage>
                <GalleryPage />
              </AnimatedPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <AnimatedPage>
                <Profile />
                <BottomNav />
              </AnimatedPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="/licence"
          element={
            <ProtectedRoute>
              <AnimatedPage>
                <Licence />
              </AnimatedPage>
            </ProtectedRoute>
          }
        />

        <Route
          path="/collections"
          element={
            <ProtectedRoute>
              <AnimatedPage>
                <Collections />
                <BottomNav />
              </AnimatedPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="/collection/:collectionId"
          element={
            <ProtectedRoute>
              <AnimatedPage>
                <CollectionDetail />
              </AnimatedPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="/collection/liked"
          element={
            <ProtectedRoute>
              <AnimatedPage>
                <LikedCollection />
              </AnimatedPage>
            </ProtectedRoute>
          }
        />

        {/* --- START OF NEW PROTECTED PORTAL ROUTES --- */}
        <Route
          path="/portals"
          element={
            <ProtectedRoute>
              <AnimatedPage>
                <Portals />
                <BottomNav />
              </AnimatedPage>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/liked/:clientId"
          element={
            <ProtectedRoute>
              <AnimatedPage>
                <ClientLiked />
              </AnimatedPage>
            </ProtectedRoute>
          }
        />
        {/* --- END OF NEW PROTECTED PORTAL ROUTES --- */}
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
